import { type ComputedRef, computed } from 'vue'
import { useInfiniteQuery } from '@tanstack/vue-query'
import { useDrainPages } from '@/composables/useDrainPages'
import { fetchBusinessServiceTypes, type BusinessServiceType } from '@/api/businessServiceTypes'
import { serviceTypesPollingInterval } from '@/config/api'

export interface FlatBusinessServiceType extends BusinessServiceType {
  // A `BusinessServiceType` with its parent category id alongside it, after
  // flattening `fetchBusinessServiceTypes`' grouped-by-category pages — a
  // group's `business_service_type_id` only ever needs a flat id lookup.

  business_service_name_id: string
}

/**
 * Drains the full `['businessServiceTypes']` list, flattening each page's
 * category groups into a single flat array — used to resolve a group's
 * `business_service_type_id` into display labels (the "Виды занятий" tags),
 * the same full-catalog-lookup need `useAllBookingTypes`/`useAllPerformers`
 * serve for their own ids.
 *
 * @param options.enabled - `GET /business-service-types` requires
 * `BusinessServiceTypesView`, which a caller's viewer isn't guaranteed to
 * hold — e.g. the Groups pane, reached via `ClientsView`/`GroupsView`
 * alone. Pass a reactive `hasRole(...)` check so a 403 doesn't trip the
 * shared axios interceptor's app-wide navigation; defaults to always-enabled.
 */
export function useAllBusinessServiceTypes(options?: { enabled?: ComputedRef<boolean> | boolean }) {
  const query = useInfiniteQuery({
    queryKey: ['businessServiceTypes'],
    queryFn: ({ pageParam }) => fetchBusinessServiceTypes(pageParam as number | undefined),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.has_next_page && lastPage.next_cursor != null ? lastPage.next_cursor : undefined,
    enabled: options?.enabled ?? true,
    staleTime: serviceTypesPollingInterval,
    refetchInterval: serviceTypesPollingInterval,
    refetchIntervalInBackground: false,
  })

  useDrainPages(query)

  const businessServiceTypes = computed<FlatBusinessServiceType[]>(() =>
    (query.data.value?.pages ?? []).flatMap((page) =>
      (page.items ?? []).flatMap((group) =>
        group.business_service_types.map((type) => ({
          ...type,
          business_service_name_id: group.business_service_name_id,
        })),
      ),
    ),
  )

  const isDraining = computed(() => query.isPending.value || query.hasNextPage.value === true)

  return {
    businessServiceTypes,
    isPending: query.isPending,
    isError: query.isError,
    isDraining,
  }
}
